import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import PureDataComponent from '@aytm/lib/common/components/pure-component'
import { viewport, preprocessText, escapeText } from '@aytm/lib/common/func'

import LightBox from './light-box'

export const popupDefaultWidth = 200
export const popupDefaultHeight = 160

import '@aytm/lib/rfd-ui/styles/basic/image-popup.scss'

const POPUP_PADDING = 5
const FRAME_PADDING = 7
const SCROLLING_PADDING = 30
const LABEL_HEIGHT = 40

export default class ImagePopup extends PureDataComponent {
  static propTypes = {
    src: PropTypes.string,
    imageProps: PropTypes.object,
    label: PropTypes.string,
    altText: PropTypes.string,
    show: PropTypes.bool,
    scrolling: PropTypes.bool,
    loadingIcon: PropTypes.string,
    failedIcon: PropTypes.string,
    closeIcon: PropTypes.string,
    onLoad: PropTypes.func,
    onError: PropTypes.func,
    onClose: PropTypes.func,
    closeOnClick: PropTypes.bool
  }

  constructor(props) {
    super(props)
    this.onLoad = this.onLoad.bind(this)
    this.onError = this.onError.bind(this)
    this.handleWindowResize = this.handleWindowResize.bind(this)
    this.resizePreload = this.resizePreload.bind(this)
    this.resize = this.resize.bind(this)
    this.handleClose = this.handleClose.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)

    this.animate = true
    this.bodyElm = document.querySelector('body')
    this.bodyElmOverflowBackup = this.bodyElm.style.overflow
    this.state = {
      loading: true,
      loaded: false,
      imageWidth: popupDefaultWidth,
      imageHeight: popupDefaultHeight,
      intrinsicWidth: 0,
      intrinsicHeight: 0
    }
  }

  onLoad(event) {
    const target = event.target
    this.setState({ intrinsicWidth: target.naturalWidth, intrinsicHeight: target.naturalHeight })

    setTimeout(() => {
      const { onLoad } = this.props
      if ('function' == typeof onLoad) {
        onLoad()
      }
      this.resize()
    }, 10)

    this.setState({ loading: false, loaded: true })
  }

  onError() {
    this.setState({ intrinsicWidth: 0, intrinsicHeight: 0 })

    setTimeout(() => {
      const { onError } = this.props
      if ('function' == typeof onError) {
        onError()
      }
      this.resize()
    }, 10)

    this.setState({ loading: false, loaded: false })
  }

  handleWindowResize() {
    this.resize()
  }

  resizePreload() {
    const { label } = this.props

    if (this.refImageFrame) {
      const fWidth = this.refImageFrame.offsetWidth
      const fHeight = this.refImageFrame.offsetHeight

      this.setState({
        imageWidth: fWidth - 2 * POPUP_PADDING - 2 * FRAME_PADDING,
        imageHeight: fHeight - 2 * POPUP_PADDING - 2 * FRAME_PADDING - (label ? LABEL_HEIGHT : 0)
      })
    }
  }

  resize() {
    const { label, scrolling } = this.props
    const { loaded, intrinsicWidth, intrinsicHeight } = this.state

    if (this.refImageFrame) {
      const v = viewport()
      const maxIWidth =
        v.w - 2 * POPUP_PADDING - 2 * FRAME_PADDING - SCROLLING_PADDING - (scrolling ? SCROLLING_PADDING : 0)
      const maxIHeight = v.h - 2 * POPUP_PADDING - 2 * FRAME_PADDING - SCROLLING_PADDING - (label ? LABEL_HEIGHT : 0)

      if (loaded) {
        const img =
          this.refImageFrame.querySelector('.rfd-ui-image-popup-image') || this.refImageFrame.querySelector('img')

        if (img) {
          let iWidth = Math.max(60, intrinsicWidth)
          let iHeight = Math.max(60, intrinsicHeight)

          const ratio = iWidth / iHeight

          if (iWidth > maxIWidth) {
            iWidth = maxIWidth
            iHeight = iWidth / ratio
          }
          if (iHeight > maxIHeight) {
            iHeight = maxIHeight
            !scrolling && (iWidth = iHeight * ratio)
          }

          this.setState({ imageWidth: iWidth, imageHeight: iHeight })

          if (this.animate) {
            const refImageFrame = this.refImageFrame

            refImageFrame.classList.add('rfd-ui-image-popup-frame-before-animate')
            setTimeout(() => {
              refImageFrame.classList.remove('rfd-ui-image-popup-frame-before-animate')
              refImageFrame.classList.add('rfd-ui-image-popup-frame-animate')
              setTimeout(() => {
                refImageFrame.classList.remove('rfd-ui-image-popup-frame-animate')
              }, 500)
            }, 100)
            this.animate = false
          }
        } else this.resizePreload()
      } else this.resizePreload()
    }
  }

  handleClose() {
    this.animate = true

    const { onClose } = this.props

    if ('function' == typeof onClose) {
      onClose()
    }
  }

  handleKeyDown(event) {
    if ('Escape' === event.key) this.handleClose()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    super.UNSAFE_componentWillReceiveProps(nextProps)

    if (nextProps.show && !this.props.show) {
      if (this.bodyElm) {
        this.bodyElmOverflowBackup = this.bodyElm.style.overflow
        this.bodyElm.style.setProperty('overflow', 'hidden')
      }
      window.addEventListener('resize', this.handleWindowResize)
      window.addEventListener('keydown', this.handleKeyDown)
    } else if (!nextProps.show && this.props.show) {
      if (this.bodyElm) {
        this.bodyElm.style.setProperty('overflow', this.bodyElmOverflowBackup || null)
      }
      window.removeEventListener('resize', this.handleWindowResize)
      window.removeEventListener('keydown', this.handleKeyDown)
    }

    this.resize()
  }

  componentDidMount() {
    super.componentDidMount()

    const { show } = this.props

    if (show) {
      if (this.bodyElm) {
        this.bodyElmOverflowBackup = this.bodyElm.style.overflow
        this.bodyElm.style.setProperty('overflow', 'hidden')
      }
      window.addEventListener('resize', this.handleWindowResize)
      window.addEventListener('keydown', this.handleKeyDown)
    } else {
      if (this.bodyElm) {
        this.bodyElm.style.setProperty('overflow', this.bodyElmOverflowBackup || null)
      }
      window.removeEventListener('resize', this.handleWindowResize)
      window.removeEventListener('keydown', this.handleKeyDown)
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount()

    if (this.bodyElm) {
      this.bodyElm.style.setProperty('overflow', this.bodyElmOverflowBackup || null)
    }
    window.removeEventListener('resize', this.handleWindowResize)
    window.removeEventListener('keydown', this.handleKeyDown)
  }

  render() {
    const {
      src,
      imageProps,
      label,
      altText,
      show,
      scrolling,
      loadingIcon = 'icon-aytmfd-spin aytmfd-spin',
      failedIcon = 'icon-aytmfd-delete',
      closeIcon = 'icon-aytmfd-delete',
      closeOnClick = false
    } = this.props
    const { loading, loaded, imageWidth, imageHeight } = this.state

    if (show) {
      const iWidth = imageWidth + (scrolling ? SCROLLING_PADDING : 0)
      const iHeight = imageHeight + (label && loaded ? LABEL_HEIGHT : 0)

      return (
        <LightBox onClose={this.handleClose} fullSize={scrolling}>
          <div
            className={classNames('rfd-ui-image-popup-frame', {
              'rfd-ui-image-popup-frame-loading': loading,
              'rfd-ui-image-popup-frame-loaded': loaded,
              'rfd-ui-image-popup-frame-scrolling': loaded && scrolling
            })}
            ref={this.saveRef('ImageFrame')}
            style={{
              width: `${Math.max(popupDefaultWidth, iWidth)}px`,
              height: `${Math.max(popupDefaultHeight, iHeight)}px`,
              margin: `-${iHeight / 2 + FRAME_PADDING}px 0 0 -${iWidth / 2 + FRAME_PADDING}px`,
              padding: `${FRAME_PADDING}px`
            }}
            onClick={closeOnClick ? this.handleClose : null}
            data-testid="SW-Image-Popup"
          >
            <div className="rfd-ui-image-popup-close-button" onClick={this.handleClose}>
              <i className={closeIcon} />
            </div>
            <div className="rfd-ui-image-popup-image">
              <img
                {...imageProps}
                src={src}
                width={`${imageWidth}px`}
                height={scrolling ? 'auto' : `${imageHeight}px`}
                onLoad={this.onLoad}
                onError={this.onError}
                style={closeOnClick ? { cursor: 'pointer' } : null}
                data-testid="SW-Image"
                alt={altText || ''}
              />
            </div>
            {loading && !loaded && (
              <div className="rfd-ui-image-popup-image-loader">
                <i className={loadingIcon} />
              </div>
            )}
            {!loading && !loaded && (
              <div className="rfd-ui-image-popup-image-failed">
                <i className={failedIcon} />
              </div>
            )}
            {label && loaded && (
              <div
                className="rfd-ui-image-popup-image-label"
                style={{ width: `${imageWidth}px`, height: `${LABEL_HEIGHT}px` }}
                dangerouslySetInnerHTML={{ __html: preprocessText(escapeText(label)) }}
              />
            )}
          </div>
        </LightBox>
      )
    } else return null
  }
}
